/**
 * ActivityPhases (§2.7) — collapsible, design-system-compliant activity view.
 *
 * Renders the phase rows produced by `reduceActivityPhases`. It is UI-agnostic
 * about the source of the state: tender chat feeds it today; the editor panel
 * adopts it unchanged in stage 4.
 *
 * Design system: tinted `bg-muted/30` rounded-lg collapsible, `text-xs`.
 * Auto-expanded while streaming; collapsed after completion (unless a phase
 * failed, in which case it stays open so the failure is visible).
 *
 * Colour appears in exactly one place: a single-hue iris sweep travelling
 * through the running phase's icon and label. Completed and failed rows keep the
 * neutral palette, so the block gets quieter as work finishes rather than
 * accumulating accent. Nothing else animates.
 */

import { useEffect, useState } from 'react'
import { Asterisk, Check, AlertCircle, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  type ActivityPhase,
  type PhaseState,
  summarizePhases,
  activeLabel,
  activePhase,
  hasFailedPhase,
} from '@/lib/activity-phases'

// ===================
// Types
// ===================

interface ActivityPhasesProps {
  phases: PhaseState
  isStreaming: boolean
}

// ===================
// Component
// ===================

export function ActivityPhases({ phases, isStreaming }: ActivityPhasesProps) {
  const rows = phases.phases
  const allDone = rows.length > 0 && rows.every((p) => p.status !== 'running')
  const failed = hasFailedPhase(phases)

  const [isOpen, setIsOpen] = useState(true)

  // Expand while streaming; collapse once everything is done — but keep a failed
  // turn open so the alert row stays in view (contract 5: failed rows do not hide).
  useEffect(() => {
    if (isStreaming || !allDone || failed) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [isStreaming, allDone, failed])

  if (rows.length === 0) return null

  // While streaming, narrate the live phase (incl. "Answering" once the answer
  // begins); only show the collapsed summary once the turn has settled.
  const triggerLabel = allDone && !isStreaming
    ? summarizePhases(phases) ?? 'Done'
    : activeLabel(phases)

  // More than one phase can be `running` at once — the reducer leaves
  // Understanding open until the answer streams, so it overlaps Searching for
  // most of a turn. The sweep marks the ONE phase the trigger is narrating, so
  // the row and the label can never disagree about what is happening now.
  const active = activePhase(phases)
  const liveKind = active?.status === 'running' ? active.kind : null

  return (
    <div className="rounded-lg bg-muted/30 px-2.5 py-1.5">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center gap-1.5 w-full py-0.5 text-meta',
              'hover:text-foreground transition-colors rounded-md',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
          >
            {!allDone ? (
              <SweepIcon />
            ) : failed ? (
              <AlertCircle className="h-3 w-3 shrink-0 text-destructive" data-testid="activity-header-failed" />
            ) : (
              <Check className="h-3 w-3 shrink-0 text-muted-foreground" data-testid="activity-header-done" />
            )}
            <span className={cn('flex-1 text-left truncate', !allDone && 'animate-shimmer-ai')}>
              {triggerLabel}
            </span>
            <ChevronDown
              className={cn(
                'h-3 w-3 shrink-0 transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="ml-1.5 border-l border-border/60 pl-3 py-1 space-y-0.5">
            {rows.map((phase) => (
              <PhaseRow key={phase.kind} phase={phase} isLive={phase.kind === liveKind} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

// ===================
// Phase Row
// ===================

function PhaseRow({ phase, isLive }: { phase: ActivityPhase; isLive: boolean }) {
  return (
    <div className="flex items-start gap-1.5 py-0.5 min-h-[20px]">
      <PhaseIcon status={phase.status} isLive={isLive} />
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            'block text-xs leading-tight truncate',
            // Three tiers: the live phase sweeps, a superseded running phase
            // stays emphasised but still, everything settled goes muted. Colour
            // exists only in the first.
            isLive && 'animate-shimmer-ai',
            !isLive && phase.status === 'running' && 'text-foreground',
            !isLive && phase.status !== 'running' && 'text-muted-foreground'
          )}
        >
          {phase.title}
        </span>
        {phase.detail && (
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 line-clamp-2">
            {phase.detail}
          </p>
        )}
      </div>
    </div>
  )
}

// ===================
// Phase Icon
// ===================

function PhaseIcon({ status, isLive }: { status: ActivityPhase['status']; isLive: boolean }) {
  switch (status) {
    case 'running':
      return (
        <div className="mt-[3px] shrink-0" data-testid="phase-icon-running">
          {isLive ? (
            <SweepIcon />
          ) : (
            // Started but superseded: still, so the panel never has two things
            // animating at once.
            <Asterisk className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
      )
    case 'completed':
      return (
        <div className="mt-[3px] shrink-0" data-testid="phase-icon-completed">
          <Check className="h-3 w-3 text-muted-foreground/60" />
        </div>
      )
    case 'failed':
      return (
        <div className="mt-[3px] shrink-0" data-testid="phase-icon-failed">
          <AlertCircle className="h-3 w-3 text-destructive" />
        </div>
      )
  }
}

// ===================
// Sweep Icon
// ===================

/**
 * The live-phase marker. Not a lucide component: the asterisk is drawn as a CSS
 * mask so it can share the label's gradient and keyframe. A real `<svg>` can't
 * take `background-clip`, so it would need its own animation on its own clock —
 * which is what made the old breathe-plus-shimmer pairing read as busy.
 */
function SweepIcon() {
  return <span className="block h-3 w-3 shrink-0 animate-sweep-icon" aria-hidden="true" />
}
